/**
 * Case Search Dashboard
 * Updated: 2026-04-29
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Stack,
  Divider,
} from "@mui/material";
import {
  AccountBalanceWallet as WalletIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Gavel as GavelIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  NavigateNext as NextIcon,
  Work as LawyerIcon,
  Groups as LitigantIcon,
  CalendarMonth as CauselistIcon,
  Visibility as VisibilityIcon,
  Balance as ScaleIcon,
  MenuBook as MenuBookIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import ECourtsApi from "Service/ECourts.api";
import CaseApi from "Service/Case.api";
import { setAlertData, startLoading, stopLoading } from "../../Redux/Screen/Action";
import DateTime from "Utils/DateTime";

// --- eCourts API Enums ---
const ENUMS = {
  STATES_UT_CODES: {
    "AN": "Andaman & Nicobar Islands", "AP": "Andhra Pradesh", "AR": "Arunachal Pradesh", "AS": "Assam",
    "BR": "Bihar", "CG": "Chhattisgarh", "CH": "Chandigarh", "DD": "Dadra & Nagar Haveli and Daman & Diu",
    "DL": "Delhi", "GA": "Goa", "GJ": "Gujarat", "HP": "Himachal Pradesh", "HR": "Haryana", "JH": "Jharkhand",
    "JK": "Jammu & Kashmir", "KA": "Karnataka", "KL": "Kerala", "LD": "Ladakh", "LW": "Lakshadweep",
    "MG": "Meghalaya", "MH": "Maharashtra", "MN": "Manipur", "MP": "Madhya Pradesh", "MZ": "Mizoram",
    "NL": "Nagaland", "OD": "Odisha", "PB": "Punjab", "PY": "Puducherry", "RJ": "Rajasthan",
    "SC": "India (Supreme Court)", "SK": "Sikkim", "TN": "Tamil Nadu", "TR": "Tripura", "TS": "Telangana",
    "UK": "Uttarakhand", "UP": "Uttar Pradesh", "WB": "West Bengal"
  },
  HIGH_COURT_CODES: {
    "APHC01": "High Court, Andhra Pradesh", "BRHC01": "High Court, Bihar", "CGHC01": "High Court, Chhattisgarh",
    "DLHC01": "High Court, Delhi", "GAHC01": "High Court, Goa (Branch 1)", "GAHC02": "High Court, Goa (Branch 2)",
    "GAHC03": "High Court, Goa (Branch 3)", "GAHC04": "High Court, Goa (Branch 4)", "GJHC24": "High Court, Gujarat",
    "HBHC01": "Punjab & Haryana, Special (High Court)", "HCBM01": "Bombay, High Court", "HCBM02": "Bombay, High Court (Branch 2)",
    "HCBM03": "Bombay, High Court (Branch 3)", "HCBM04": "Bombay, High Court (Branch 4)", "HCBM05": "Bombay High Court (Kolhapur Bench)",
    "HCBM06": "Bombay, High Court (Branch 6)", "HCMA01": "Madras, High Court", "HCMD01": "High Court of Madhya Pradesh",
    "HPHC01": "High Court of Himachal Pradesh", "JHHC01": "High Court of Jharkhand", "JKHC01": "High Court of Jammu & Kashmir (Jammu)",
    "JKHC02": "High Court of Jammu & Kashmir (Srinagar)", "KAHC01": "High Court of Karnataka (Bangalore)",
    "KAHC02": "High Court of Karnataka (Dharwad Bench)", "KAHC03": "High Court of Karnataka (Gulbarga Bench)",
    "KLHC01": "High Court of Kerala", "MLHC01": "High Court of Meghalaya", "MNHC01": "High Court of Manipur",
    "MPHC01": "High Court of Madhya Pradesh (Jabalpur)", "MPHC02": "High Court of Madhya Pradesh (Indore Bench)",
    "MPHC03": "High Court of Madhya Pradesh (Gwalior Bench)", "ODHC01": "High Court of Orissa",
    "PHHC01": "High Court of Punjab and Haryana", "RJHC01": "High Court of Rajasthan (Jodhpur)",
    "RJHC02": "High Court of Rajasthan (Jaipur Bench)", "SKHC01": "High Court of Sikkim", "TRHC01": "High Court of Tripura",
    "UKHC01": "High Court of Uttarakhand", "UPHC01": "Allahabad High Court (Allahabad)",
    "UPHC02": "Allahabad High Court (Lucknow Bench)", "WBCHCA": "Calcutta High Court (Appellate Side)",
    "WBCHCJ": "Calcutta High Court (Original Side)", "WBCHCO": "Calcutta High Court (Original Civil Jurisdiction)",
    "WBCHCP": "Calcutta High Court (Circuit Bench Port Blair)", "WBHC01": "Calcutta High Court"
  },
  COURT_TYPES: {
    "DISTRICT_COURT": "District Court", "HIGH_COURT": "High Court", "NCLAT": "NCLAT", "NCLT": "NCLT", "SUPREME_COURT": "Supreme Court"
  },
  CASE_STATUSES: {
    "ABATED": "Abated", "ADJOURNED": "Adjourned", "ADMITTED": "Admitted", "ALLOWED": "Allowed",
    "ALLOWED_NO_COSTS": "Allowed (No Costs)", "APPEARANCE": "Appearance", "ARGUMENTS": "Arguments",
    "BAIL_GRANTED": "Bail Granted", "BAIL_REJECTED": "Bail Rejected", "CHARGE_FRAMED": "Charge Framed",
    "CLOSED": "Closed", "COMPLIANCE_PENDING": "Compliance Pending", "COMPROMISE": "Compromise",
    "CONTEMPT_PENDING": "Contempt Pending", "COUNTER_AFFIDAVIT_FILED": "Counter Affidavit Filed",
    "CROSS_EXAMINATION": "Cross Examination", "CURATIVE_PENDING": "Curative Pending", "DECREED": "Decreed",
    "DEFECTIVE": "Defective", "DEFENCE_EVIDENCE": "Defence Evidence", "DISMISSED": "Dismissed",
    "DISMISSED_AS_WITHDRAWN": "Dismissed as Withdrawn", "DISMISSED_IN_DEFAULT": "Dismissed in Default",
    "DISMISSED_NO_COSTS": "Dismissed (No Costs)", "DISPOSED": "Disposed", "EVIDENCE": "Evidence",
    "EXECUTION_PENDING": "Execution Pending", "FILED": "Filed", "FIRST_HEARING": "First Hearing",
    "FOR_ADMISSION": "For Admission", "FOR_JUDGMENT": "For Judgment", "FOR_ORDERS": "For Orders",
    "FOR_STEPS": "For Steps", "FRAMING_OF_ISSUES": "Framing of Issues", "HEARING": "Hearing",
    "INFRUCTUOUS": "Infructuous", "INTERIM_ORDER_PASSED": "Interim Order Passed", "JUDGMENT": "Judgment",
    "LISTED": "Listed", "LOK_ADALAT": "Lok Adalat", "LPA_PENDING": "LPA Pending",
    "MAINTENANCE_PENDING": "Maintenance Pending", "MEDIATION": "Mediation", "MENTIONED": "Mentioned",
    "NOTICE_ISSUED": "Notice Issued", "NOT_ADMITTED": "Not Admitted", "PARTLY_ALLOWED": "Partly Allowed",
    "PART_HEARD": "Part Heard", "PENDING": "Pending", "PENDING_AFTER_NOTICE": "Pending After Notice",
    "PENDING_BEFORE_NOTICE": "Pending Before Notice", "PIL_PENDING": "PIL Pending",
    "PLAINTIFF_EVIDENCE": "Plaintiff Evidence", "PROSECUTION_EVIDENCE": "Prosecution Evidence",
    "READY_FOR_REGISTRATION": "Ready for Registration", "REGISTERED": "Registered", "REJECTED": "Rejected",
    "REJOINDER_FILED": "Rejoinder Filed", "REMANDED": "Remanded", "RESERVED": "Reserved",
    "REVIEW_PENDING": "Review Pending", "RULE_ABSOLUTE": "Rule Absolute", "RULE_DISCHARGED": "Rule Discharged",
    "SERVICE_PENDING": "Service Pending", "STAYED": "Stayed", "SUMMONS_ISSUED": "Summons Issued",
    "TRANSFERRED": "Transferred", "TRIAL": "Trial", "UNKNOWN": "Unknown Status", "WITHDRAWN": "Withdrawn",
    "WRITTEN_STATEMENT_FILED": "Written Statement Filed"
  },
  JUDICIAL_SECTIONS: {
    "CIV": "Civil Section", "CRIM": "Criminal Section", "BAIL": "Bail Section", "APP": "Appeal Section", "WRIT": "Writ Section"
  },
  CASE_TYPES: {
    "ABA": "Anticipatory Bail Application", "ADM": "Admission Matter", "AFT_APPEAL": "Appeal from Armed Forces Tribunal",
    "ARB_A": "Arbitration Appeal", "ARB_PET": "Arbitration Petition", "AS": "Appeal from Subordinate Court",
    "Arb": "Arbitration Case (DC)", "Arb_Pet": "Arbitration Petition", "BA": "Bail Application", "CA": "Civil Appeal",
    "CAPP_AT": "Company Appeal (AT)", "CAPP_AT_INS": "Company Appeal (AT) (Insolvency)", "CAPP_CA": "Company Appeal (Companies Act)",
    "CAPP_IBC": "Company Appeal (IBC)", "CAV": "Caveat", "CA_CA": "Company Application (Companies Act)",
    "CA_IBC": "Company Application (IBC)", "CA_MA_CA": "Company Application - Merger & Amalgamation (Companies Act)",
    "CC": "Criminal Complaint Case", "CMA": "Civil Miscellaneous Appeal", "CMAppl": "Civil Miscellaneous Application",
    "COMP_APP": "Compensation Application", "COMP_APPEAL": "Competition Appeal (AT)", "COM_S": "Commercial Suit",
    "CONMT": "Contempt Petition", "CONT_AT": "Contempt Case (AT)", "CONT_CA": "Contempt Petition (Companies Act)",
    "CONT_IBC": "Contempt Petition (IBC)", "COP": "Company Petition", "CP_CA": "Company Petition (Companies Act)",
    "CP_IBC": "Company Petition (IBC)", "CP_MA_CA": "Company Petition - Merger & Amalgamation (Companies Act)",
    "CR": "Civil Revision (short form)", "CRA": "Criminal Appeal (DC)", "CRLP": "Criminal Leave Petition",
    "CRLRC": "Criminal Revision Case", "CRL_A": "Criminal Appeal", "CRL_MA": "Criminal Miscellaneous Application (DC)",
    "CROSS_APPEAL_CA": "Cross Appeal (Companies Act)", "CROSS_APPEAL_IBC": "Cross Appeal (IBC)",
    "CROSS_CA": "Cross Application (Companies Act)", "CROSS_IBC": "Cross Application (IBC)", "CRP": "Civil Revision Petition",
    "CRR": "Criminal Revision (DC)", "CR_MISC": "Criminal Miscellaneous", "CR_REV": "Criminal Revision",
    "CR_WJC": "Criminal Writ Jurisdiction Case", "CS": "Civil Suit", "CURP": "Curative Petition",
    "CWJC": "Civil Writ Jurisdiction Case", "C_REV": "Civil Revision", "DREF": "Death Reference",
    "DV": "Domestic Violence Case", "EA": "Execution Application (Civil)", "EP": "Election Petition",
    "FA": "First Appeal", "FEMA": "FEMA Case", "GP": "Guardianship Petition", "G_APP": "General Appeal",
    "HCP": "Habeas Corpus Petition", "IA": "Interlocutory Application", "IA_CA": "Interlocutory Application (Companies Act)",
    "IA_IBC": "Interlocutory Application (IBC)", "IA_IBC_DIS": "Interlocutory Application - Disposal (IBC)",
    "IA_IBC_LIQ": "Interlocutory Application - Liquidation (IBC)", "IA_IBC_PLAN": "Interlocutory Application - Plan (IBC)",
    "IA_LIQ_PR": "IA Liquidation Progress Report", "IB_PREPACK": "Insolvency & Bankruptcy Pre-Packaged",
    "INT_CA": "Intervention Petition (Companies Act)", "INT_IBC": "Intervention Petition (IBC)", "IP": "Insolvency Petition",
    "ITA": "Income Tax Appeal", "LC": "Land Acquisition Case", "LPA": "Letters Patent Appeal", "MA": "Miscellaneous Application (Civil)",
    "MACA": "Motor Accident Claims Appeal", "MA_CA": "Miscellaneous Application (Companies Act)", "MA_IBC": "Miscellaneous Application (IBC)",
    "MCOC": "Maintenance Case", "MCrA": "Miscellaneous Criminal Application", "MFA": "Miscellaneous First Appeal",
    "MJC": "Miscellaneous Judicial Case (Civil)", "NDPS": "NDPS Act Case", "NGT_APPEAL": "Appeal from National Green Tribunal",
    "OMP": "Original Miscellaneous Petition", "OS": "Original Suit", "PIL": "Public Interest Litigation (HC)",
    "PP": "Probate Petition", "RA_CA": "Restoration Application (Companies Act)", "RA_IBC": "Restoration Application (IBC)",
    "RC": "Rent Control Case", "RCP_CA": "Restored Company Petition (Companies Act)", "RCP_IBC": "Restored Company Petition (IBC)",
    "RCS_A": "Regular Civil Suit - Class A", "RCS_B": "Regular Civil Suit - Class B", "RCT": "Regular Criminal Trial",
    "REF": "Reference (Advisory Jurisdiction)", "REHAB_CA": "Rehabilitation Petition (Companies Act)",
    "REHAB_IBC": "Rehabilitation Petition (IBC)", "RERA": "RERA Appeal", "REST_APP": "Restoration Application",
    "REV_APP": "Review Application", "REV_CA": "Review Application (Companies Act)", "REV_IBC": "Review Application (IBC)",
    "RFA": "Regular First Appeal", "RP_C": "Review Petition (Civil)", "RP_CRL": "Review Petition (Criminal)",
    "RSA": "Regular Second Appeal", "RULE63_APPEAL": "Rule 63 Appeal", "SA": "Second Appeal", "SC": "Sessions Case",
    "SCA": "Special Civil Application", "SC_ST": "SC/ST Act Case", "SLP_C": "Special Leave Petition (Civil)",
    "SLP_CRL": "Special Leave Petition (Criminal)", "ST": "Sessions Trial", "SUM": "Summary Trial",
    "Special_Ref": "Special Reference Case", "TA_AT": "Transfer Application (AT)", "TA_CA": "Transfer Application (Companies Act)",
    "TA_IBC": "Transfer Application (IBC)", "TOP_AT": "Transfer of Proceedings (AT)", "TP_C": "Transfer Petition (Civil)",
    "TP_CA": "Transfer Petition (Companies Act)", "TP_CRL": "Transfer Petition (Criminal)", "TP_IBC": "Transfer Petition (IBC)",
    "TS": "Testamentary Suit", "Tax_Ref": "Tax Reference Case", "UNKNOWN": "Unrecognized case type",
    "VL_IBC": "Voluntary Liquidation (IBC)", "WA": "Writ Appeal", "WPC_O": "Writ Petition (Original Jurisdiction)",
    "WP_C": "Writ Petition (Civil)", "WP_CRL": "Writ Petition (Criminal)", "WP_C_PIL": "Writ Petition (Public Interest Litigation)",
    "WP_PIL": "Writ Petition (Public Interest Litigation - simplified)", "XOBJ": "Cross Objection"
  }
};

/**
 * Single-page Case Result View
 * Displays all case details in a professional, unified dashboard layout.
 */
const CaseResultView = ({ results, onBack }: { results: any, onBack?: () => void }) => {
  const dispatch = useDispatch();
  const rawData = results?.data || results;
  const actualData = rawData?.data || rawData;
  const caseData = actualData?.courtCaseData || actualData;
  const aiAnalysis = caseData?.aiAnalysis || actualData?.aiAnalysis || actualData?.files?.files?.[0]?.aiAnalysis;
  const caseTypeLookup = actualData?.descriptions?.enumLookup?.caseType || {};
  const fullCaseType = caseTypeLookup[caseData.caseType] || caseData.caseTypeRaw || caseData.caseType;
  // const filesData = actualData?.files?.files || []; // Unused for now
  if (!caseData || (!caseData.caseNumber && !caseData.cnr && !caseData.filingNumber && !caseData.id)) {
    return (
      <Alert severity="warning" sx={{ mt: 4, borderRadius: 3 }}>
        Case details found but could not be parsed. Please contact support.
      </Alert>
    );
  }

  const petitioners = caseData.petitioners || [];
  const respondents = caseData.respondents || [];
  const title = `${petitioners[0] || "Petitioner"} vs. ${respondents[0] || "Respondent"}`;
  const judges = caseData.judges || [];
  const acts = caseData.actsAndSections || [];
  const hearings = caseData.historyOfCaseHearings || [];
  const orders = caseData.judgmentOrders || [];

  const handleViewOrder = async (orderUrl: string) => {
    const filename = orderUrl.split('/').pop();
    if (!filename) return;

    dispatch(startLoading({ loading: true, loadtext: "Fetching Order..." }));
    try {
      const res = await ECourtsApi.getOrderDownloadInfo(caseData.cnr, filename);
      dispatch(stopLoading());

      // Since we used excel: true, res.data is a Blob
      const blob = res.data;
      if (blob && blob.size > 0) {
        if (blob.type === "application/pdf") {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.target = '_blank';
          // link.download = filename; // Optional: Force download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          // If it's not a PDF, it might be JSON metadata in a blob
          const text = await blob.text();
          try {
            const json = JSON.parse(text);
            const downloadMsg = json.data?.message || json.message || "Order metadata fetched.";
            const downloadFile = json.data?.downloadFilename || filename;

            dispatch(setAlertData({
              alertMessage: `${downloadMsg} Filename: ${downloadFile}`,
              alertVariant: "success",
              openAlert: true
            }));
          } catch (e) {
            // Fallback for raw text
            dispatch(setAlertData({ alertMessage: "Order data fetched (Non-PDF).", alertVariant: "info", openAlert: true }));
          }
        }
      }
    } catch (err: any) {
      dispatch(stopLoading());
      dispatch(setAlertData({ alertMessage: err.message || "Failed to fetch order.", alertVariant: "error", openAlert: true }));
    }
  };

  return (
    <Box sx={{ mt: 4, mb: 8 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8.5 }}>
          <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0 2px 12px rgba(0,0,0,0.03)", mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
              {onBack && (
                <Button
                  startIcon={<NextIcon sx={{ transform: 'rotate(180deg)' }} />}
                  onClick={onBack}
                  sx={{ mb: 2, fontWeight: 700, color: '#64748b' }}
                >
                  Back to Results
                </Button>
              )}
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Typography variant="h4" fontWeight={800} color="#283165" sx={{ lineHeight: 1.2 }}>
                  {title}
                </Typography>
                {/* <Button variant="text" size="small" endIcon={<ExpandMoreIcon />} sx={{ color: '#283165', fontWeight: 600 }}>View Orders</Button> */}
              </Box>

              <Box display="flex" gap={3} flexWrap="wrap" mb={2}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Court:</Typography>
                  <Typography variant="body2" fontWeight={700} color="#283165" sx={{ ml: 1, display: 'inline' }}>{caseData.courtName || "N/A"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Judges:</Typography>
                  <Typography variant="body2" fontWeight={700} color="#283165" sx={{ ml: 1, display: 'inline' }}>{judges.join(", ") || "N/A"}</Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }} display="block">Case Type:</Typography>
                  <Typography variant="body2" fontWeight={700}>{fullCaseType || "N/A"}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }} display="block">Reg no:</Typography>
                  <Typography variant="body2" fontWeight={700}>{caseData.registrationNumber || "N/A"}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }} display="block">Filing no:</Typography>
                  <Typography variant="body2" fontWeight={700}>{caseData.filingNumber || "N/A"}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }} display="block">CNR:</Typography>
                  <Box sx={{ color: '#fff', px: 1, py: 0.2, borderRadius: 1, display: 'inline-block' }}>
                    <Typography variant="body2" fontWeight={800}>{caseData.cnr || "N/A"}</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box mt={2}>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Case Category:</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ ml: 1, display: 'inline' }}>
                  {acts.map((a: any) => a.act).join(", ") || "General Litigation"}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0 2px 12px rgba(0,0,0,0.03)", mb: 3 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScaleIcon sx={{ color: '#283165', fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={800} color="#283165">Parties & Advocates</Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ borderLeft: '3px solid #283165', pl: 2, mb: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                      <LitigantIcon sx={{ color: '#283165', fontSize: 18 }} />
                      <Typography variant="caption" fontWeight={800} color="#283165">Petitioner</Typography>
                    </Box>
                    {petitioners.map((p: string, i: number) => (
                      <Box key={i} sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 2, mb: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={800} color="#94a3b8">{i + 1}</Typography>
                        <Typography variant="body2" fontWeight={700}>{p}</Typography>
                      </Box>
                    ))}

                    <Box display="flex" alignItems="center" gap={1} mt={2} mb={1}>
                      <LawyerIcon sx={{ color: '#283165', fontSize: 18 }} />
                      <Typography variant="caption" fontWeight={800} color="#283165">Petitioner Advocate</Typography>
                    </Box>
                    {caseData.petitionerAdvocates?.map((a: string, i: number) => (
                      <Box key={i} sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 2, mb: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={800} color="#94a3b8">{i + 1}</Typography>
                        <Typography variant="body2" fontWeight={700}>{a}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ borderLeft: '3px solid #283165', pl: 2, mb: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                      <LitigantIcon sx={{ color: '#283165', fontSize: 18 }} />
                      <Typography variant="caption" fontWeight={800} color="#283165">Respondent</Typography>
                    </Box>
                    {respondents.map((r: string, i: number) => (
                      <Box key={i} sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 2, mb: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={800} color="#94a3b8">{i + 1}</Typography>
                        <Typography variant="body2" fontWeight={700}>{r}</Typography>
                      </Box>
                    ))}

                    <Box display="flex" alignItems="center" gap={1} mt={2} mb={1}>
                      <LawyerIcon sx={{ color: '#283165', fontSize: 18 }} />
                      <Typography variant="caption" fontWeight={800} color="#283165">Respondent Advocate</Typography>
                    </Box>
                    {caseData.respondentAdvocates?.map((a: string, i: number) => (
                      <Box key={i} sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 2, mb: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={800} color="#94a3b8">{i + 1}</Typography>
                        <Typography variant="body2" fontWeight={700}>{a}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0 2px 12px rgba(0,0,0,0.03)", mb: 3 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon sx={{ color: '#283165', fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={800} color="#283165">Case History with Orders</Typography>
            </Box>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ position: 'relative', py: 4 }}>
                <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '2px', bgcolor: '#e2e8f0', transform: 'translateX(-50%)' }} />

                {caseData.caseStatus === "DISPOSED" && (
                  <Box sx={{ position: 'relative', mb: 6, display: 'flex', justifyContent: 'center' }}>
                    <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 0, zIndex: 2, bgcolor: '#fff', border: '2px solid #283165', p: 0.5, borderRadius: '50%' }}>
                      <ScaleIcon sx={{ color: '#283165', fontSize: 24 }} />
                    </Box>
                    <Box sx={{ width: '40%', ml: 'auto', mt: 1, pl: 4 }}>
                      <Box sx={{ bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 2, p: 2 }}>
                        <Typography variant="body2" fontWeight={800} color="#166534">
                          Disposed - {DateTime.ToDisplayDate(caseData.decisionDate) || "N/A"}
                        </Typography>
                        <Typography variant="caption" fontWeight={600} color="#15803d">Final Status</Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {(() => {
                  const displayedOrders = new Set();
                  return [...hearings].reverse().map((h: any, i: number) => {
                    const hearingDate = h.hearingDate || h.businessOnDate;
                    const matchingOrder = orders.find((o: any) =>
                      o.orderDate === hearingDate && !displayedOrders.has(o.orderUrl)
                    );

                    if (matchingOrder) {
                      displayedOrders.add(matchingOrder.orderUrl);
                    }

                    return (
                      <Box key={i} sx={{ position: 'relative', mb: 4, display: 'flex', alignItems: 'flex-start' }}>
                        <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 10, zIndex: 2, bgcolor: '#fff', border: '2px solid #283165', p: 0.2, borderRadius: '50%' }}>
                          <HistoryIcon sx={{ color: '#283165', fontSize: 16 }} />
                        </Box>

                        <Box sx={{ width: '40%', pr: 4 }}>
                          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #283165' }}>
                            <Typography variant="body2" fontWeight={800} color="#283165" mb={0.5}>
                              {DateTime.ToDisplayDate(hearingDate)}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <DescriptionIcon sx={{ fontSize: 16, color: '#283165' }} />
                              <Typography variant="body2" fontWeight={700}>{h.purposeOfListing || "N/A"}</Typography>
                            </Box>
                            <Typography variant="caption" fontWeight={600} color="#64748b">Before: {h.judge || "Hon'ble Court"}</Typography>
                          </Paper>
                        </Box>

                        <Box sx={{ width: '40%', ml: 'auto', pl: 4 }}>
                          {matchingOrder && (
                            <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, p: 2 }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <DescriptionIcon sx={{ fontSize: 16, color: '#283165' }} />
                                  <Typography variant="body2" fontWeight={800}>Order({orders.indexOf(matchingOrder) + 1}) - {DateTime.ToDisplayDate(matchingOrder.orderDate)}</Typography>
                                </Box>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{ fontSize: '0.65rem', fontWeight: 800 }}
                                  onClick={() => matchingOrder.orderUrl && handleViewOrder(matchingOrder.orderUrl)}
                                >
                                  View
                                </Button>
                              </Box>
                              <Typography variant="caption" fontWeight={700} color="#64748b">{matchingOrder.orderType?.toUpperCase() || "ORDER"}</Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  });
                })()}

                {caseData.firstHearingDate && (
                  <Box sx={{ position: 'relative', mb: 4, display: 'flex', alignItems: 'flex-start' }}>
                    <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 10, zIndex: 2, bgcolor: '#fff', border: '2px solid #283165', p: 0.2, borderRadius: '50%' }}>
                      <HistoryIcon sx={{ color: '#283165', fontSize: 16 }} />
                    </Box>
                    <Box sx={{ width: '40%', pr: 4 }}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #283165' }}>
                        <Typography variant="body2" fontWeight={800} color="#283165" mb={0.5}>
                          {DateTime.ToDisplayDate(caseData.firstHearingDate)}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <DescriptionIcon sx={{ fontSize: 16, color: '#283165' }} />
                          <Typography variant="body2" fontWeight={700}>First Hearing</Typography>
                        </Box>
                        <Typography variant="caption" fontWeight={600} color="#64748b">Initial hearing scheduled</Typography>
                      </Paper>
                    </Box>
                  </Box>
                )}

                {caseData.registrationDate && (
                  <Box sx={{ position: 'relative', mb: 4, display: 'flex', alignItems: 'flex-start' }}>
                    <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 10, zIndex: 2, bgcolor: '#fff', border: '2px solid #283165', p: 0.2, borderRadius: '50%' }}>
                      <HistoryIcon sx={{ color: '#283165', fontSize: 16 }} />
                    </Box>
                    <Box sx={{ width: '40%', pr: 4 }}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #283165' }}>
                        <Typography variant="body2" fontWeight={800} color="#283165" mb={0.5}>
                          {DateTime.ToDisplayDate(caseData.registrationDate)}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <DescriptionIcon sx={{ fontSize: 16, color: '#283165' }} />
                          <Typography variant="body2" fontWeight={700}>Case Registered</Typography>
                        </Box>
                        <Typography variant="caption" fontWeight={600} color="#64748b">Registration No: {caseData.registrationNumber || "N/A"}</Typography>
                      </Paper>
                    </Box>
                  </Box>
                )}

                <Box sx={{ position: 'relative', mt: 4, display: 'flex', justifyContent: 'center' }}>
                  <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: 0, zIndex: 2, bgcolor: '#fff', border: '2px solid #283165', p: 0.5, borderRadius: '50%' }}>
                    <GavelIcon sx={{ color: '#283165', fontSize: 24 }} />
                  </Box>
                  <Box sx={{ width: '40%', ml: 'auto', pl: 4 }}>
                    <Box sx={{ bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 2, p: 2 }}>
                      <Typography variant="body2" fontWeight={800} color="#1e40af">Case Filed</Typography>
                      <Typography variant="caption" fontWeight={600} display="block">{DateTime.ToDisplayDate(caseData.filingDate) || "N/A"}</Typography>
                      <Typography variant="caption">Filing No: {caseData.filingNumber || "N/A"}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {aiAnalysis && (
            <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0", boxShadow: "0 2px 12px rgba(0,0,0,0.03)", mb: 3 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
                <GavelIcon sx={{ color: '#283165', fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={800} color="#283165">Deep AI Analysis & Insights</Typography>
              </Box>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight={800} gutterBottom color="#283165">Ratio Decidendi</Typography>
                <Paper sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 3, mb: 4, borderLeft: '6px solid #283165' }}>
                  <Typography variant="body1" sx={{ fontStyle: 'italic', lineHeight: 1.8 }}>
                    "{aiAnalysis.deep_legal_substance_context?.arguments_and_reasoning_analysis?.ratio_decidendi_extracted?.statement || "AI Analysis pending for this order."}"
                  </Typography>
                </Paper>

                <Grid container spacing={4}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" fontWeight={800} mb={2}>Key Legal Issues</Typography>
                    {aiAnalysis.deep_legal_substance_context?.core_legal_content_analysis?.primary_legal_issues_identified?.map((issue: string, i: number) => (
                      <Box key={i} sx={{ mb: 2, p: 2, bgcolor: '#fdfdfd', border: '1px solid #f1f5f9', borderRadius: 2, display: 'flex', gap: 2 }}>
                        <Typography color="primary" fontWeight={800}>0{i + 1}</Typography>
                        <Typography variant="body2" fontWeight={600}>{issue}</Typography>
                      </Box>
                    ))}
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" fontWeight={800} mb={2}>Actionable Alerts</Typography>
                    {aiAnalysis.intelligent_insights_analytics?.order_significance_and_impact_assessment?.actionable_alerts?.map((alert: any, i: number) => (
                      <Box key={i} sx={{ mb: 2, p: 2, bgcolor: '#fff4e5', border: '1px solid #ffe2b7', borderRadius: 2 }}>
                        <Typography variant="body2" fontWeight={800} color="#9a4303" gutterBottom>{alert.action_required}</Typography>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="caption" fontWeight={700}>Deadline: {alert.deadline || "ASAP"}</Typography>
                          <Chip label={alert.responsible_party} size="small" sx={{ fontSize: '0.6rem', height: 20 }} />
                        </Box>
                      </Box>
                    ))}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}


        </Grid>

        <Grid size={{ xs: 12, md: 3.5 }}>
          <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0", p: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={800} color="#283165" mb={2} display="flex" alignItems="center" gap={1}>
              <SearchIcon fontSize="small" /> Similar Case Search
            </Typography>

            <Box mb={3}>
              <Typography variant="caption" fontWeight={800} color="#94a3b8" display="flex" alignItems="center" gap={1} mb={1.5}>
                <PeopleIcon sx={{ fontSize: 16 }} /> Same Parties
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {petitioners.slice(0, 2).map((p: string, i: number) => (
                  <Chip key={i} label={p} size="small" variant="outlined" sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }} />
                ))}
                {respondents.slice(0, 1).map((r: string, i: number) => (
                  <Chip key={i} label={r} size="small" variant="outlined" sx={{ justifyContent: 'flex-start', height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }} />
                ))}
              </Box>
            </Box>

            <Box mb={3}>
              <Typography variant="caption" fontWeight={800} color="#94a3b8" display="flex" alignItems="center" gap={1} mb={1.5}>
                <LawyerIcon sx={{ fontSize: 16 }} /> Lawyers
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {caseData.petitionerAdvocates?.map((a: string, i: number) => (
                  <Chip key={i} label={`Advocate ${a}`} size="small" sx={{ bgcolor: '#eff6ff', color: '#1e40af', fontWeight: 700 }} />
                ))}
              </Box>
            </Box>

            <Box mb={3}>
              <Typography variant="caption" fontWeight={800} color="#94a3b8" display="flex" alignItems="center" gap={1} mb={1.5}>
                <LawyerIcon sx={{ fontSize: 16 }} /> Judges
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {judges.map((j: string, i: number) => (
                  <Chip key={i} label={j} size="small" sx={{ bgcolor: '#f8fafc', fontWeight: 600 }} />
                ))}
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" fontWeight={800} color="#94a3b8" display="flex" alignItems="center" gap={1} mb={1.5}>
                <MenuBookIcon sx={{ fontSize: 16 }} /> Acts & Sections
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2}>
                <Chip
                  label={caseData?.caseTypeSub}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: '#283165',
                    fontWeight: 600,
                    height: 'auto', // Allows the chip to grow vertically
                    '& .MuiChip-label': {
                      display: 'block', // Changes from default 'inline-block'
                      whiteSpace: 'normal', // Allows text to wrap
                      padding: '8px 12px', // Adds some vertical breathing room
                    },
                  }}
                />
              </Box>
            </Box>
          </Card>


        </Grid>
      </Grid>
    </Box>
  );
};

const CaseSelectionList = ({
  list,
  onSelect,
  page,
  totalPages,
  onPageChange,
  totalHits
}: {
  list: any[],
  onSelect: (cnr: string) => void,
  page: number,
  totalPages: number,
  onPageChange: (event: any, page: number) => void,
  totalHits?: number
}) => {
  return (
    <Card sx={{ mt: 4, borderRadius: 4 }}>
      <Box sx={{ p: 3, bgcolor: "#f8f9fa", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={700}>Search Results ({totalHits || list.length})</Typography>
        {totalPages > 1 && (
          <Pagination count={totalPages} page={page} onChange={onPageChange} color="primary" size="small" />
        )}
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f8f9fa" }}>
              <TableCell sx={{ fontWeight: 700 }}>CNR / Case No</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Parties</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Filing Date</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((item: any, i: number) => {
              const d = item?.courtCaseData || item?.data || item;
              const itemCnr = d?.cnr || d?.caseNumber || d?.filingNumber || d?.id || "View Details";
              return (
                <TableRow key={i} hover onClick={() => onSelect(itemCnr)} sx={{ cursor: 'pointer' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{itemCnr}</TableCell>
                  <TableCell><Chip label={d?.caseType || "N/A"} size="small" variant="outlined" /></TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" noWrap fontWeight={600}>
                      {d?.petitioners?.[0] || d?.partyName || "N/A"}
                    </Typography>
                    <Typography variant="caption" color="text.primary">vs</Typography>
                    <Typography variant="body2" noWrap>
                      {d?.respondents?.[0] || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell>{d?.filingDate || "N/A"}</TableCell>
                  <TableCell><Button variant="outlined" size="small" endIcon={<NextIcon />} sx={{ fontWeight: 700 }}>View Details</Button></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', borderTop: '1px solid #f1f5f9' }}>
          <Pagination count={totalPages} page={page} onChange={onPageChange} color="primary" />
        </Box>
      )}
    </Card>
  );
};

const CaseSearchDashboard = () => {
  const dispatch = useDispatch();
  const { myInfo } = useSelector((state: any) => state.user);

  const [balance, setBalance] = useState<number | null>(null);
  const [cnrNumber, setCnrNumber] = useState("");
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [results, setResults] = useState<any>(null);
  const [rechargeModalOpen, setRechargeModalOpen] = useState(false);
  const [walletPlans, setWalletPlans] = useState<any[]>([]);
  const [orderTypeId, setOrderTypeId] = useState<number | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalHits, setTotalHits] = useState(0);
  const [searchListResults, setSearchListResults] = useState<any>(null);
  const [isViewingDetails, setIsViewingDetails] = useState(false);

  useEffect(() => {
    fetchBalance();
    fetchPlans();
    fetchRecentActivity();
  }, []);

  // --- Advanced Filter States ---
  const [selectedCourtCodes, setSelectedCourtCodes] = useState<string[]>([]);
  const [selectedCaseTypes, setSelectedCaseTypes] = useState<string[]>([]);
  const [selectedCaseStatuses, setSelectedCaseStatuses] = useState<string[]>([]);
  const [selectedJudicialSections, setSelectedJudicialSections] = useState<string[]>([]);
  const [selectedCourtTypes, setSelectedCourtTypes] = useState<string[]>([]);
  const [selectedStateCodes, setSelectedStateCodes] = useState<string[]>([]);

  const [filingDateFrom, setFilingDateFrom] = useState("");
  const [filingDateTo, setFilingDateTo] = useState("");
  const [nextHearingDateFrom, setNextHearingDateFrom] = useState("");
  const [nextHearingDateTo, setNextHearingDateTo] = useState("");
  const [decisionDateFrom, setDecisionDateFrom] = useState("");
  const [decisionDateTo, setDecisionDateTo] = useState("");

  const clearFilters = () => {
    setSelectedCourtCodes([]);
    setSelectedCaseTypes([]);
    setSelectedCaseStatuses([]);
    setSelectedJudicialSections([]);
    setSelectedCourtTypes([]);
    setSelectedStateCodes([]);
    setFilingDateFrom("");
    setFilingDateTo("");
    setNextHearingDateFrom("");
    setNextHearingDateTo("");
    setDecisionDateFrom("");
    setDecisionDateTo("");
  };

  const fetchBalance = async () => {
    try {
      const res = await ECourtsApi.getWalletBalance();
      if (res.success) {
        setBalance(res.data.balance);
      }
    } catch (err) {
      console.error("Error fetching balance:", err);
    }
  };

  const fetchPlans = async () => {
    try {
      const orderRes = await CaseApi.fetchOrderTypes();
      if (orderRes.success) {
        const walletOrder = orderRes.data.find((o: any) => o.name === "Wallet Recharge");
        if (walletOrder) {
          setOrderTypeId(walletOrder.id);
          const caseRes = await CaseApi.fetchCaseTypes();
          if (caseRes.success) {
            const plans = caseRes.data.filter((c: any) => c.orderTypeId === walletOrder.id);
            setWalletPlans(plans);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const res = await ECourtsApi.getRecentActivity();
      if (res.success) {
        setRecentActivity(Array.isArray(res.data) ? res.data : (res.data?.data || []));
      }
    } catch (err) {
      console.error("Error fetching activity:", err);
    }
  };

  const handleHistoryClick = async (item: any) => {
    dispatch(startLoading({ loading: true, loadtext: "Loading History..." }));
    try {
      const res = await ECourtsApi.getHistoryItem(item.id);
      dispatch(stopLoading());
      if (res.success) {
        const raw = res.data?.data || res.data;
        setResults(raw);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err: any) {
      dispatch(stopLoading());
      dispatch(setAlertData({ alertMessage: err.message, alertVariant: "error", openAlert: true }));
    }
  };

  const handlePlanSelect = async (plan: any) => {
    setRechargeModalOpen(false);
    dispatch(startLoading({ loading: true, loadtext: "Initiating Recharge..." }));

    try {
      const payload = {
        clientId: myInfo.id,
        caseTypeId: plan.id,
        orderTypeId: orderTypeId,
        description: `Wallet Top-Up: ${plan.name}`,
      };

      const res = await CaseApi.createCase(payload);
      dispatch(stopLoading());
      if (res.success) {
        window.location.href = res.data.paymentSessionUrl;
      }
    } catch (err: any) {
      dispatch(stopLoading());
      dispatch(setAlertData({ alertMessage: err.message || "Failed to initiate recharge.", alertVariant: "error", openAlert: true }));
    }
  };

  const handleCnrSearch = async (targetCnr?: string) => {
    const cnr = targetCnr || cnrNumber;
    if (!cnr) return;

    dispatch(startLoading({ loading: true, loadtext: "Searching Case..." }));
    try {
      const res = await ECourtsApi.searchByCNR(cnr);
      dispatch(stopLoading());
      if (res.success) {
        setResults(res.data);
        setIsViewingDetails(true); // Always view details for a specific CNR search
        fetchBalance();
        fetchRecentActivity();
      }
    } catch (err: any) {
      dispatch(stopLoading());
      dispatch(setAlertData({ alertMessage: err.message || "Search failed.", alertVariant: "error", openAlert: true }));
    }
  };

  const handleGlobalSearch = (targetPage = 1) => {
    if (!globalSearchQuery) return;

    const query = globalSearchQuery.trim();
    setPage(targetPage);
    dispatch(startLoading({ loading: true, loadtext: "Searching..." }));

    const isCnr = /^[a-zA-Z0-9]{16}$/.test(query);

    let searchPromise;
    if (isCnr) {
      searchPromise = ECourtsApi.searchByCNR(query);
    } else {
      // Construct search parameters based on category
      const searchParams: {
        query?: string;
        advocates?: string;
        judges?: string;
        litigants?: string;
        page?: number;
        pageSize?: number;
        courtCodes?: string[];
        caseTypes?: string[];
        caseStatuses?: string[];
        judicialSections?: string[];
        courtTypes?: string[];
        stateCodes?: string[];
        filingDateFrom?: string;
        filingDateTo?: string;
        nextHearingDateFrom?: string;
        nextHearingDateTo?: string;
        decisionDateFrom?: string;
        decisionDateTo?: string;
      } = {
        page: targetPage,
        pageSize: 20
      };

      if (selectedCategory === 'Lawyer') searchParams.advocates = query;
      else if (selectedCategory === 'Judge') searchParams.judges = query;
      else if (selectedCategory === 'Litigant') searchParams.litigants = query;
      else searchParams.query = query;

      // Add Advanced Filters
      if (selectedCaseTypes.length > 0) searchParams.caseTypes = selectedCaseTypes;
      if (selectedStateCodes.length > 0) searchParams.stateCodes = selectedStateCodes;
      if (selectedCourtCodes.length > 0) searchParams.courtCodes = selectedCourtCodes;
      if (selectedCaseStatuses.length > 0) searchParams.caseStatuses = selectedCaseStatuses;
      if (selectedJudicialSections.length > 0) searchParams.judicialSections = selectedJudicialSections;
      if (selectedCourtTypes.length > 0) searchParams.courtTypes = selectedCourtTypes;

      if (filingDateFrom) searchParams.filingDateFrom = filingDateFrom;
      if (filingDateTo) searchParams.filingDateTo = filingDateTo;
      if (nextHearingDateFrom) searchParams.nextHearingDateFrom = nextHearingDateFrom;
      if (nextHearingDateTo) searchParams.nextHearingDateTo = nextHearingDateTo;
      if (decisionDateFrom) searchParams.decisionDateFrom = decisionDateFrom;
      if (decisionDateTo) searchParams.decisionDateTo = decisionDateTo;
      console.log("searchParams", searchParams);

      searchPromise = ECourtsApi.searchByPartyName(searchParams);
    }

    searchPromise
      .then((res: any) => {
        dispatch(stopLoading());
        if (res.success) {
          const rawData = res.data?.data || res.data;
          const actualData = rawData?.data || rawData;

          // Check if it's a list or a single result
          const list = Array.isArray(actualData) ? actualData :
            Array.isArray(actualData?.results) ? actualData.results :
              Array.isArray(actualData?.cases) ? actualData.cases : null;

          if (list) {
            setSearchListResults(res.data);

            // Robust pagination extraction
            const tp = rawData?.totalPages || actualData?.totalPages ||
              (rawData?.totalHits ? Math.ceil(rawData.totalHits / 20) : 1);
            const th = rawData?.totalHits || actualData?.totalHits || list.length;

            setTotalPages(tp);
            setTotalHits(th);
            setIsViewingDetails(false);
          } else {
            setResults(res.data);
            setIsViewingDetails(true);
          }

          fetchBalance();
          fetchRecentActivity();
        }
      })
      .catch((err: any) => {
        dispatch(stopLoading());
        dispatch(setAlertData({ alertMessage: err.message || "Search failed.", alertVariant: "error", openAlert: true }));
      });
  };

  const handlePageChange = (event: any, value: number) => {
    handleGlobalSearch(value);
  };

  return (
    <Box sx={{ p: 4, maxWidth: 1200, mx: "auto", minHeight: '100vh', bgcolor: '#fbfcfe' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={800} color="#283165">Search Dashboard</Typography>
        <Typography variant="body2" color="text.primary">Search across all Indian courts using CNR, Party Name, or Case Number.</Typography>
      </Box>

      <Card sx={{ borderRadius: 4, mb: 6, boxShadow: "0 10px 40px rgba(0,0,0,0.04)", overflow: 'visible' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            {[
              { label: 'All', icon: <SearchIcon fontSize="small" /> },
              { label: 'Lawyer', icon: <LawyerIcon fontSize="small" /> },
              { label: 'Judge', icon: <GavelIcon fontSize="small" /> },
              { label: 'Litigant', icon: <LitigantIcon fontSize="small" /> },
              { label: 'Causelist', icon: <CauselistIcon fontSize="small" /> },
            ].map((cat) => (
              <Button
                key={cat.label}
                variant={selectedCategory === cat.label ? "contained" : "outlined"}
                startIcon={cat.icon}
                onClick={() => setSelectedCategory(cat.label)}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: selectedCategory === cat.label ? '#283165' : '#f1f5f9',
                  color: selectedCategory === cat.label ? '#fff' : '#64748b',
                  border: 'none',
                  '&:hover': {
                    bgcolor: selectedCategory === cat.label ? '#1c234b' : '#e2e8f0',
                    border: 'none',
                  }
                }}
              >
                {cat.label}
              </Button>
            ))}
          </Box>

          <Paper
            variant="outlined"
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 0.5,
              borderRadius: 3,
              borderColor: '#e2e8f0',
              bgcolor: '#fff',
              '&:focus-within': { borderColor: '#283165', boxShadow: '0 0 0 4px rgba(40, 49, 101, 0.1)' }
            }}
          >
            <TextField
              fullWidth
              placeholder="Search cases, lawyers, judges..."
              value={globalSearchQuery}
              onChange={(e) => setGlobalSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleGlobalSearch(1)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '& .MuiInputBase-input': { px: 2, py: 1.5, fontWeight: 500 }
              }}
            />
            <IconButton
              onClick={() => handleGlobalSearch(1)}
              sx={{
                bgcolor: '#283165',
                color: '#fff',
                borderRadius: 2,
                m: 0.5,
                '&:hover': { bgcolor: '#1c234b' }
              }}
            >
              <SearchIcon />
            </IconButton>
          </Paper>

          {/* Advanced Filters Accordion */}
          <Accordion variant="outlined" sx={{ mt: 2, borderRadius: 3, borderColor: '#e2e8f0', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
              <Box display="flex" alignItems="center" gap={1}>
                <FilterIcon fontSize="small" color="primary" />
                <Typography fontWeight={600} color="#334155">Advanced Filters</Typography>
                {(selectedCourtCodes.length > 0 || selectedCaseStatuses.length > 0 || selectedCourtTypes.length > 0 || selectedStateCodes.length > 0 || filingDateFrom || nextHearingDateFrom) && (
                  <Chip label="Active" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ borderTop: '1px solid #f1f5f9' }}>
              <Grid container spacing={3}>
                {/* Categorical Selects */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>State / UT</InputLabel>
                    <Select
                      multiple
                      value={selectedStateCodes}
                      onChange={(e) => setSelectedStateCodes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      input={<OutlinedInput label="State / UT" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, color: "#000" }}>
                          {selected.map((value) => (
                            <Chip key={value} label={ENUMS.STATES_UT_CODES[value as keyof typeof ENUMS.STATES_UT_CODES] || value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {Object.entries(ENUMS.STATES_UT_CODES).map(([code, name]) => (
                        <MenuItem key={code} value={code} sx={{ color: "#000" }}>
                          <Checkbox checked={selectedStateCodes.indexOf(code) > -1} />
                          <ListItemText primary={name} secondary={code} sx={{ color: "#000" }} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Court / High Court</InputLabel>
                    <Select
                      multiple
                      value={selectedCourtCodes}
                      onChange={(e) => setSelectedCourtCodes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      input={<OutlinedInput label="Court / High Court" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={ENUMS.HIGH_COURT_CODES[value as keyof typeof ENUMS.HIGH_COURT_CODES] || value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {Object.entries(ENUMS.HIGH_COURT_CODES).map(([code, name]) => (
                        <MenuItem key={code} value={code}>
                          <Checkbox checked={selectedCourtCodes.indexOf(code) > -1} />
                          <ListItemText primary={name} secondary={code} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Court Type</InputLabel>
                    <Select
                      multiple
                      value={selectedCourtTypes}
                      onChange={(e) => setSelectedCourtTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      input={<OutlinedInput label="Court Type" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={ENUMS.COURT_TYPES[value as keyof typeof ENUMS.COURT_TYPES] || value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {Object.entries(ENUMS.COURT_TYPES).map(([code, name]) => (
                        <MenuItem key={code} value={code}>
                          <Checkbox checked={selectedCourtTypes.indexOf(code) > -1} />
                          <ListItemText primary={name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Case Type</InputLabel>
                    <Select
                      multiple
                      value={selectedCaseTypes}
                      onChange={(e) => setSelectedCaseTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      input={<OutlinedInput label="Case Type" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={ENUMS.CASE_TYPES[value as keyof typeof ENUMS.CASE_TYPES] || value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {Object.entries(ENUMS.CASE_TYPES).map(([code, name]) => (
                        <MenuItem key={code} value={code}>
                          <Checkbox checked={selectedCaseTypes.indexOf(code) > -1} />
                          <ListItemText primary={name} secondary={code} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Case Status</InputLabel>
                    <Select
                      multiple
                      value={selectedCaseStatuses}
                      onChange={(e) => setSelectedCaseStatuses(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      input={<OutlinedInput label="Case Status" />}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {Object.entries(ENUMS.CASE_STATUSES).map(([code, name]) => (
                        <MenuItem key={code} value={code}>
                          <Checkbox checked={selectedCaseStatuses.indexOf(code) > -1} />
                          <ListItemText primary={name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Section</InputLabel>
                    <Select
                      multiple
                      value={selectedJudicialSections}
                      onChange={(e) => setSelectedJudicialSections(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      input={<OutlinedInput label="Section" />}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {Object.entries(ENUMS.JUDICIAL_SECTIONS).map(([code, name]) => (
                        <MenuItem key={code} value={code}>
                          <Checkbox checked={selectedJudicialSections.indexOf(code) > -1} />
                          <ListItemText primary={name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}><Divider /></Grid>

                {/* Date Ranges */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" fontWeight={700} color="text.primary" display="block" mb={1}>FILING DATE RANGE</Typography>
                  <Stack direction="row" spacing={1}>
                    <TextField type="date" size="small" fullWidth value={filingDateFrom} onChange={(e) => setFilingDateFrom(e.target.value)} label="From" InputLabelProps={{ shrink: true }} />
                    <TextField type="date" size="small" fullWidth value={filingDateTo} onChange={(e) => setFilingDateTo(e.target.value)} label="To" InputLabelProps={{ shrink: true }} />
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" fontWeight={700} color="text.primary" display="block" mb={1}>HEARING DATE RANGE</Typography>
                  <Stack direction="row" spacing={1}>
                    <TextField type="date" size="small" fullWidth value={nextHearingDateFrom} onChange={(e) => setNextHearingDateFrom(e.target.value)} label="From" InputLabelProps={{ shrink: true }} />
                    <TextField type="date" size="small" fullWidth value={nextHearingDateTo} onChange={(e) => setNextHearingDateTo(e.target.value)} label="To" InputLabelProps={{ shrink: true }} />
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" fontWeight={700} color="text.primary" display="block" mb={1}>DECISION DATE RANGE</Typography>
                  <Stack direction="row" spacing={1}>
                    <TextField type="date" size="small" fullWidth value={decisionDateFrom} onChange={(e) => setDecisionDateFrom(e.target.value)} label="From" InputLabelProps={{ shrink: true }} />
                    <TextField type="date" size="small" fullWidth value={decisionDateTo} onChange={(e) => setDecisionDateTo(e.target.value)} label="To" InputLabelProps={{ shrink: true }} />
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters} color="inherit">Clear Filters</Button>
                  <Button size="small" variant="contained" onClick={() => handleGlobalSearch(1)} startIcon={<SearchIcon />}>Apply Filters</Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {results && isViewingDetails && (
        <Box sx={{ mb: 6 }}>
          <CaseResultView
            results={results}
            onBack={searchListResults ? () => setIsViewingDetails(false) : undefined}
          />
        </Box>
      )}

      {searchListResults && !isViewingDetails && (
        <Box sx={{ mb: 6 }}>
          {(() => {
            const rawData = searchListResults?.data || searchListResults;
            const actualData = rawData?.data || rawData;
            const list = Array.isArray(actualData) ? actualData :
              Array.isArray(actualData?.results) ? actualData.results :
                Array.isArray(actualData?.cases) ? actualData.cases :
                  Array.isArray(rawData?.list) ? rawData.list : null;

            if (list && list.length > 0) {
              return (
                <CaseSelectionList
                  list={list}
                  page={page}
                  totalPages={totalPages}
                  totalHits={totalHits}
                  onPageChange={handlePageChange}
                  onSelect={(caseCnr: string) => {
                    setCnrNumber(caseCnr);
                    handleCnrSearch(caseCnr);
                  }}
                />
              );
            }
            return null;
          })()}
        </Box>
      )}

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={700} color="#1e293b">Your recent activity</Typography>
            <Button size="small" variant="text" sx={{ fontWeight: 600 }}>View All</Button>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 4,
              maxHeight: 500,
              overflowY: 'auto',
              bgcolor: '#fff',
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#e2e8f0', borderRadius: 10 }
            }}
          >
            {(!Array.isArray(recentActivity) || recentActivity.length === 0) ? (
              <Box p={6} sx={{ textAlign: 'center' }}>
                <Typography color="text.primary">No recent activity found.</Typography>
              </Box>
            ) : (
              Array.isArray(recentActivity) && recentActivity.map((item, idx) => (
                <Box
                  key={item.id}
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: idx === recentActivity.length - 1 ? 'none' : '1px solid #f1f5f9',
                    '&:hover': { bgcolor: '#f8fafc' },
                    cursor: 'pointer'
                  }}
                  onClick={() => handleHistoryClick(item)}
                >
                  <Box display="flex" alignItems="center" gap={2} sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ bgcolor: '#f1f5f9', p: 1, borderRadius: 2 }}>
                      {item.searchType === 'CNR' ? <DescriptionIcon color="action" /> : <SearchIcon color="action" />}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body1" fontWeight={700} noWrap color="#334155">
                        {item.title || item.searchQuery}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip label={item.status} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#e2e8f0' }} />
                        <Typography variant="caption" color="text.primary" noWrap>
                          {item.subtitle} • {new Date(item.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <IconButton size="small"><VisibilityIcon sx={{ fontSize: 18 }} /></IconButton>
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="h6" fontWeight={700} color="#1e293b" mb={2}>Wallet Status</Typography>
          <Card
            sx={{
              borderRadius: 4,
              background: "linear-gradient(135deg, #283165 0%, #39468e 100%)",
              color: "#fff",
              boxShadow: "0 10px 30px rgba(40, 49, 101, 0.2)"
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                <Box>
                  <Typography variant="overline" color="white" sx={{ opacity: 0.8, fontWeight: 700, letterSpacing: 1 }}>BALANCE</Typography>
                  <Typography variant="h3" color="white" fontWeight={800}>₹{balance?.toFixed(2) || '0.00'}</Typography>
                </Box>
                <WalletIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setRechargeModalOpen(true)}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  color: '#fff',
                  fontWeight: 700,
                  py: 1.5,
                  borderRadius: 2,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                }}
              >
                Add Funds
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={rechargeModalOpen} onClose={() => setRechargeModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>Select Top-Up Plan</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {walletPlans.map((plan: any) => (
              <Grid size={{ xs: 12 }} key={plan.id}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    borderRadius: 3,
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: "#283165",
                      bgcolor: "rgba(40, 49, 101, 0.02)",
                      transform: "translateY(-2px)"
                    }
                  }}
                  onClick={() => handlePlanSelect(plan)}
                >
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon sx={{ color: "#283165", mr: 2 }} />
                    <Typography fontWeight={600}>{plan.name}</Typography>
                  </Box>
                  <Typography fontWeight={800} color="primary" variant="h6">
                    ₹{plan.casePrice.toLocaleString()}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setRechargeModalOpen(false)} sx={{ fontWeight: 600 }}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaseSearchDashboard;
